// Configuration service for runtime configuration
interface ScantixConfig {
  API_BASE_URL: string;
  SENTRY_DSN?: string;
  SENTRY_ENVIRONMENT?: string;
  SENTRY_RELEASE?: string;
  FEATURES?: {
    BARCODE_SCANNER?: boolean;
    CAMERA_SCANNER?: boolean;
    REPORTS?: boolean;
  };
}

class ConfigService {
  private config: ScantixConfig | null = null;

  /**
   * Initialize configuration from window object
   * This is called during app initialization
   */
  async initialize(): Promise<void> {
    // Check if configuration exists on window (try both names for compatibility)
    const windowConfig =
      (
        window as unknown as {
          SCANTIX_CONFIG?: Record<string, unknown>;
          APP_CONFIG?: Record<string, unknown>;
        }
      ).SCANTIX_CONFIG ||
      (
        window as unknown as {
          SCANTIX_CONFIG?: Record<string, unknown>;
          APP_CONFIG?: Record<string, unknown>;
        }
      ).APP_CONFIG;

    if (!windowConfig) {
      console.error(
        "❌ No configuration found on window.SCANTIX_CONFIG or window.APP_CONFIG",
      );
      throw new Error("Configuration not found");
    }

    // Validate required fields
    if (!windowConfig.API_BASE_URL) {
      throw new Error("API_BASE_URL is required in configuration");
    }

    this.config = windowConfig;
    console.log("✅ Configuration loaded:", {
      apiUrl: this.config.API_BASE_URL,
    });
  }

  /**
   * Get the current configuration
   */
  getConfig(): ScantixConfig {
    if (!this.config) {
      throw new Error(
        "Configuration not initialized. Call initialize() first.",
      );
    }
    return this.config;
  }

  /**
   * Get API base URL
   */
  getApiBaseUrl(): string {
    if (!this.config) {
      throw new Error("Configuration not initialized");
    }
    return this.config.API_BASE_URL;
  }

  /**
   * Check if a feature is enabled
   */
  isFeatureEnabled(
    feature: keyof NonNullable<ScantixConfig["FEATURES"]>,
  ): boolean {
    return this.config?.FEATURES?.[feature] ?? true;
  }

  /**
   * Get Sentry configuration
   */
  getSentryConfig() {
    if (!this.config) return null;

    return {
      dsn: this.config.SENTRY_DSN,
      environment: this.config.SENTRY_ENVIRONMENT || "production",
      release: this.config.SENTRY_RELEASE || "1.0.0",
    };
  }
}

// Export singleton instance
export const configService = new ConfigService();
